import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingCart,
  Crown,
  Store,
  Upload,
  HelpCircle,
  MessageSquare,
  Search,
  MapPin,
  User,
  Bell,
  ChevronDown,
  X,
  Star,
  Check,
  ArrowRight,
  LocationOn,
} from "lucide-react";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { setCategory, setSearch } from "../redux/productFilterSlice";
import { setSubscriptionPlan } from "../redux/subscriptionSlice";
import { useGuest } from "../context/GuestContext";
import AnimatedIconButton from "./AnimatedIconButton";
import ProfileDropdown from "../components/ProfileDrop/ProfileDropdown";
import SellerVerification from "../sellers/sellerVerification/SellerVerification";

// Subscription Plans Component
function SubscriptionPlans({ onSelect }) {
  const plans = [
    {
      name: "Free",
      price: 0,
      duration: "Lifetime",
      features: ["10 products per day", "No discounts", "Basic support"],
      popular: false,
    },
    {
      name: "Pro",
      price: 299,
      duration: "Monthly",
      features: ["Unlimited products", "20% discount", "Priority support"],
      popular: true,
    },
    {
      name: "Enterprise",
      price: 999,
      duration: "Yearly",
      features: ["Unlimited products", "30% discount", "24/7 Premium Support"],
      popular: false,
    },
  ];

  return (
    <div className="p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Choose Your Plan</h2>
        <p className="text-gray-500">Select the perfect subscription for your needs</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {plans.map((plan, index) => (
          <motion.div
            key={plan.name}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -10, scale: 1.02 }}
            className={`relative p-6 rounded-2xl shadow-lg border-2 transition-all ${
              plan.popular
                ? "border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50"
                : "border-gray-100 bg-white"
            }`}
          >
            {plan.popular && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-semibold rounded-full shadow-lg">
                  Most Popular ⭐
                </span>
              </div>
            )}

            <h3 className="text-xl font-bold text-gray-800 mb-2">{plan.name}</h3>
            <div className="mb-4">
              <span className="text-4xl font-bold text-gray-800">₹{plan.price}</span>
              <span className="text-gray-500 text-sm ml-1">/{plan.duration}</span>
            </div>

            <ul className="space-y-3 mb-6">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
                    <Check size={12} />
                  </div>
                  {feature}
                </li>
              ))}
            </ul>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelect(plan)}
              className={`w-full py-3 rounded-xl font-semibold transition-all ${
                plan.popular
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30 hover:shadow-xl"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Choose {plan.name}
            </motion.button>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// Search Dropdown Component
function SearchDropdown({ products, onSelect, onClose }) {
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = [...new Set(products.map((p) => p.productType))];

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50"
    >
      <div className="p-3 border-b border-gray-100">
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 bg-gray-50 rounded-xl outline-none focus:ring-2 focus:ring-blue-100"
          autoFocus
        />
      </div>
      <div className="max-h-96 overflow-y-auto">
        {categories.map((category) => {
          const categoryProducts = products.filter(
            (p) => p.productType === category && p.name.toLowerCase().includes(searchTerm.toLowerCase())
          );
          if (categoryProducts.length === 0) return null;

          return (
            <div
              key={category}
              className="border-b border-gray-50 last:border-0"
            >
              <button
                onMouseEnter={() => setHoveredCategory(category)}
                onMouseLeave={() => setHoveredCategory(null)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-700">{category}</span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${
                    hoveredCategory === category ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence>
                {hoveredCategory === category && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-gray-50"
                  >
                    {categoryProducts.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => {
                          onSelect(product);
                          onClose();
                        }}
                        className="w-full px-8 py-2 flex items-center gap-3 hover:bg-white transition-colors"
                      >
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                        <div className="text-left">
                          <p className="text-sm font-medium text-gray-700">{product.name}</p>
                          <p className="text-xs text-gray-500">₹{product.price}</p>
                        </div>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

// Notification Bell
function NotificationBell({ count }) {
  return (
    <div className="relative">
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors relative"
      >
        <Bell size={20} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </motion.button>
    </div>
  );
}

const Navbar = ({ productsList = [] }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useSelector((state) => state.ui.location);
  const cartItems = useSelector((state) => state.cart.items);
  const cartItemsCount = cartItems.length;
  const subscription = useSelector((state) => state.subscription);
  
  const { isGuest, isSubscribed, showSubscription, setShowSubscription, startSubscriptionTimer, closeSubscription } = useGuest();

  const [userLocation, setUserLocation] = useState("Loading...");
  const [filter, setFilter] = useState("All Products");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [apiProducts, setApiProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showSellerModal, setShowSellerModal] = useState(false);
  const searchInputRef = useRef(null);

  // Products structure
  const products = [
    { name: "All Products", subcategories: [] },
    ...Array.from(
      new Map(
        apiProducts.map((p) => [
          p.productType,
          {
            name: p.productType,
            subcategories: apiProducts
              .filter((x) => x.productType === p.productType)
              .map((x) => x.productName),
          },
        ])
      ).values()
    ),
  ];

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            const city = data.address?.city || data.address?.town || data.address?.village || "Unknown";
            setUserLocation(city);
          } catch (err) {
            setUserLocation("User");
          }
        },
        () => setUserLocation("User")
      );
    } else {
      setUserLocation("User");
    }
  }, []);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch("https://localhost:44370/api/Product");
        const data = await res.json();
        setApiProducts(data);
      } catch (error) {
        console.error("Failed to fetch products", error);
      }
    };
    fetchProducts();
  }, []);

  // Handle subscription popup trigger from GuestContext timer
  useEffect(() => {
    if (showSubscription && !isLoggedIn()) {
      // User is not logged in, redirect to login
      toast.error("Please login to choose a subscription plan", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
      closeSubscription();
      navigate("/login");
    } else if (showSubscription && isLoggedIn()) {
      setShowSubscriptionModal(true);
    }
  }, [showSubscription, closeSubscription, navigate]);

  // Guest subscription timer
  useEffect(() => {
    if (isGuest && !isSubscribed) startSubscriptionTimer();
  }, [isGuest, isSubscribed, startSubscriptionTimer]);

  // Handle subscription selection
  const handleChoosePlan = (plan) => {
    toast.success(`${plan.name} Plan Selected`, {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
    });
    dispatch(setSubscriptionPlan(plan.name));
    setShowSubscriptionModal(false);
    setShowSubscription?.(false);
  };

  // Check if user is logged in
  const isLoggedIn = () => {
    const user = localStorage.getItem("user");
    return user !== null;
  };

  // Handle subscription click - require login
  const handleSubscriptionClick = () => {
    if (!isLoggedIn()) {
      toast.error("Please login to choose a subscription plan", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
      navigate("/login");
      return false;
    }
    setShowSubscriptionModal(true);
    return true;
  };

  // Handle seller click - require login
  const handleSellerClick = () => {
    if (!isLoggedIn()) {
      toast.error("Please login to become a seller", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
      navigate("/login");
      return false;
    }
    setShowSellerModal(true);
    return true;
  };

  // Export cart as CSV
  const handleExport = () => {
    if (cartItems.length === 0) {
      toast.success("Cart is empty", {
        position: "top-center",
        autoClose: 3000,
        theme: "light",
        transition: Bounce,
      });
      return;
    }

    const csvContent =
      "data:text/csv;charset=utf-8," +
      ["Product Name,Price,Quantity"]
        .concat(cartItems.map((item) => `${item.name},${item.price},${item.quantity}`))
        .join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.href = encodedUri;
    link.download = "cart_items.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />

      {/* NAVBAR */}
      <nav className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white sticky top-0 z-50 shadow-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate("/")}
              className="flex items-center gap-3 cursor-pointer"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Store className="w-6 h-6 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold">Unique Elevator</h1>
                <p className="text-[10px] text-gray-400">Premium Solutions</p>
              </div>
            </motion.div>

            {/* Location */}
            <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-white/10 rounded-xl">
              <MapPin size={16} className="text-blue-400" />
              <span className="text-sm">{userLocation}</span>
            </div>

            {/* Search Bar */}
            <div className="flex-1 max-w-xl mx-8 relative">
              <motion.div
                whileFocus={{ scale: 1.02 }}
                className="relative"
              >
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowSearchDropdown(true)}
                  onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
                  placeholder={`Search ${filter}...`}
                  className="w-full pl-12 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-full outline-none focus:bg-white/20 focus:border-white/40 transition-all placeholder-gray-400 text-sm"
                />
              </motion.div>

              <AnimatePresence>
                {showSearchDropdown && apiProducts.length > 0 && (
                  <SearchDropdown
                    products={apiProducts}
                    onSelect={(product) => {
                      dispatch(setSearch(product.name));
                      setSearchQuery(product.name);
                    }}
                    onClose={() => setShowSearchDropdown(false)}
                  />
                )}
              </AnimatePresence>
            </div>

            {/* Icons */}
            <div className="flex items-center gap-2">
              {/* Cart */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/cart")}
                className="relative p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
              >
                <ShoppingCart size={20} />
                {cartItemsCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold shadow-lg">
                    {cartItemsCount}
                  </span>
                )}
              </motion.button>

              {/* Subscription */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSubscriptionClick}
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-medium shadow-lg shadow-yellow-500/30"
              >
                <Crown size={18} />
                <span className="text-sm">{subscription.plan || "Free"}</span>
              </motion.button>

              {/* Notifications */}
              <NotificationBell count={3} />

              {/* Sell */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSellerClick}
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
              >
                <Store size={18} />
                <span className="text-sm">Sell</span>
              </motion.button>

              {/* Export */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleExport}
                className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
              >
                <Upload size={18} />
              </motion.button>

              {/* Help */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/splash")}
                className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
              >
                <HelpCircle size={18} />
              </motion.button>

              {/* Profile */}
              <ProfileDropdown />
            </div>
          </div>

          {/* Category Bar */}
          <div className="flex items-center gap-4 py-3 border-t border-white/10">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 px-4 py-1.5 bg-white/10 rounded-full text-sm hover:bg-white/20 transition-colors"
            >
              <span>{filter}</span>
              <ChevronDown size={14} />
            </button>

            <div className="flex-1 flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {["All Products", "Residential", "Commercial", "Industrial", "Accessories", "Services"].map((item) => (
                <motion.button
                  key={item}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setFilter(item);
                    dispatch(setCategory(item));
                  }}
                  className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                    filter === item
                      ? "bg-white text-gray-900"
                      : "bg-white/10 hover:bg-white/20"
                  }`}
                >
                  {item}
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Category Dropdown */}
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute left-0 right-0 top-full bg-white shadow-2xl border-t z-40"
            >
              <div className="max-w-7xl mx-auto p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                {products.slice(0, 8).map((product) => (
                  <div
                    key={product.name}
                    onMouseEnter={() => setHoveredProduct(product.name)}
                    onMouseLeave={() => setHoveredProduct(null)}
                    className="relative"
                  >
                    <button
                      onClick={() => {
                        setFilter(product.name);
                        dispatch(setCategory(product.name));
                        setShowDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium"
                    >
                      {product.name}
                    </button>

                    <AnimatePresence>
                      {hoveredProduct === product.name && product.subcategories?.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, x: 10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 10 }}
                          className="absolute left-full top-0 ml-0 md:ml-2 bg-gray-50 rounded-xl shadow-lg p-3 min-w-[200px] z-50"
                        >
                          {product.subcategories.map((sub) => (
                            <button
                              key={sub}
                              onClick={() => {
                                dispatch(setSearch(sub));
                                setShowDropdown(false);
                              }}
                              className="w-full text-left px-3 py-2 text-gray-600 hover:bg-white hover:text-blue-600 rounded-lg transition-colors text-sm"
                            >
                              {sub}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* SELLER MODAL */}
      <AnimatePresence>
        {showSellerModal && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <button
                onClick={() => setShowSellerModal(false)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
              >
                <X size={20} />
              </button>
              <SellerVerification onClose={() => setShowSellerModal(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SUBSCRIPTION MODAL */}
      <AnimatePresence>
        {(showSubscriptionModal || showSubscription) && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative bg-white w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <button
                onClick={() => {
                  setShowSubscriptionModal(false);
                  closeSubscription?.();
                }}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
              >
                <X size={20} />
              </button>
              <SubscriptionPlans onSelect={handleChoosePlan} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
